import styled from 'styled-components';
import { useState } from 'react';

// Components
import DocumentRequirementsModal from './DocumentRequirementsModal';
import SuccessfulUploadModal from './SuccessfulUploadModal';
import UpdateDocumentsModal from './UpdateDocumentsModal';
import UploadDocumentsModal from './UploadDocumentsModal';
import { useSnackbar } from 'react-simple-snackbar';
import ErrorUploadModal from './ErrorUploadModal';
import DownloadButton from './DownloadButton';
import { Text } from '../../../common/Texts';
import AwaitModal from './AwaitModal';
import { Icon } from '@iconify/react';

// Material UI
import Tooltip from '@mui/material/Tooltip';

// Styles
import { Badge, Name, TextState } from '../../../../styled-components';

// Redux
import { saveDocument, updateDocument } from '../../../../redux/api/documents';
import { documentsActions } from '../../../../redux/actions';
import { useDispatch } from 'react-redux';

// Utils
import { optionsStyle } from '../../../../utils/snackStyles';
import IaUploadDocumentsModal from './IaUploadDocumentsModal';
import DocumentRequirementsSign from './DocumentRequirementsSign';
import FontAwesomeIcon from '../../../common/FontAwesomeIcon';
import DocumentReviewDataModal from './DocumentReviewDataModal';

const Document = (props) => {
    // Props
    const { data } = props;

    const documentId = data?.idDocument;
    const documentName = data?.file_name;
    const documentType = data?.document_type;
    const documentTypeId = data?.document_type_id;
    const downloadFile = data?.documentation_url;
    const exampleFile = data?.instructive_image;
    const requirementsDetail =
        data?.text_instructive !== false ? data?.text_instructive : '';
    // Si viene un state dentro de plantilla_firma_documentación, es porque se tiene que firmar el documento
    const stateDocumentToSign = data?.document_sign_template?.state;

    // Redux
    const dispatch = useDispatch();

    // States
    const [loading, setLoading] = useState(false);
    const [openSnackbar] = useSnackbar(optionsStyle);

    const [openRequirementsModal, setOpenRequirementsModal] = useState(false);
    const [openUpdateDocumentModal, setOpenUpdateDocumentModal] =
        useState(false);
    const [openIaUpdateModal, setOpenIaUpdateModal] = useState(false);
    const [openUploadDocumentModal, setOpenUploadDocumentModal] =
        useState(false);
    const [openIaDocumentModal, setOpenIaDocumentModal] = useState(false);
    const [openSuccesModal, setOpenSuccesModal] = useState({
        isOpen: false,
        state: '',
        messageError: '',
    });
    const [openSignModal, setOpenSignModal] = useState(false);
    const [openAwaitModal, setOpenAwaitModal] = useState(false);
    const [openReviewData, setOpenReviewData] = useState({
        isOpen: false,
        passportData: null,
    });
    const [openErrorModal, setOpenErrorModal] = useState({
        isOpen: false,
        messageError: '',
    });

    const revision_id = data?.is_reviewed_by_ai;

    // ========= FUNCTIONS =========
    //Lista de requerimientos
    const handleViewRequirements = () => {
        setOpenRequirementsModal(true);
    };
    const handleCloseViewRequirements = () => {
        setOpenRequirementsModal(false);
    };

    const transformToDownloadableUrl = (url) => {
        const match = url.match(/\/d\/([a-zA-Z0-9_-]+)\//);
        if (match && match[1]) {
            const fileId = match[1];
            return `https://drive.google.com/uc?export=download&id=${fileId}`;
        }
        return null;
    };

    // Generate PDF download
    const generatePdf = (file, namePdf) => {
        const link = document.createElement('a');
        link.href = file;
        link.download = namePdf;
        link.click();
    };

    // Handle signature button click
    const handleSignatureClick = () => {
        if (stateDocumentToSign === 'completed') {
            // Download signed document
            const signedDocumentUrl =
                data?.document_sign_template?.signed_document_url ||
                downloadFile;
            if (signedDocumentUrl) {
                generatePdf(
                    transformToDownloadableUrl(signedDocumentUrl),
                    documentName
                );
            } else {
                openSnackbarText('No se pudo encontrar el documento firmado');
            }
        } else {
            // Open sign modal
            setOpenSignModal(true);
        }
    };

    //modal para cargar el archivo
    const handleClickUpload = () => {
        if (revision_id) {
            setOpenIaDocumentModal(true);
        } else {
            setOpenUploadDocumentModal(true);
        }
    };
    const handleClickUpdate = () => {
        if (revision_id) {
            setOpenIaUpdateModal(true);
        } else {
            setOpenUpdateDocumentModal(true);
        }
    };

    //download file
    const openSnackbarText = (text) => {
        openSnackbar(text);
    };

    const handleActionButton = (status = '', documentId, documentName) => {
        if (!status) {
            return;
        }
        const renderTooltip = (title, icon, action) => (
            <FileActionsButton onClick={action}>
                <Tooltip title={title} placement="top" arrow>
                    <Icon icon={icon} width="24" height="24" />
                </Tooltip>
            </FileActionsButton>
        );
        switch (status?.toLowerCase()) {
            case 'rechazado':
            case 'rechazada':
                return renderTooltip(
                    'Actualizar documentación',
                    'lucide:rotate-cw',
                    handleClickUpdate
                );
            case 'pendiente aprobación':
            case 'pendiente':
            case 'aceptado':
            case 'aceptada':
                return (
                    <DownloadButton
                        label={null}
                        idFile={data?.id}
                        nameFile={documentName}
                        setLoading={setLoading}
                        openSnackbarText={openSnackbarText}
                    />
                );
            default:
                if (stateDocumentToSign) {
                    return null;
                }
                return renderTooltip(
                    'Subir documentación',
                    'stash:folder-arrow-up',
                    handleClickUpload
                );
        }
    };

    const getBadgeContent = (status) => {
        const renderBadge = (title, icon) => (
            <Badge status={status}>
                <Icon icon={icon} width="24" height="24" />
                <Name>{title}</Name>
            </Badge>
        );
        switch (status) {
            case 'Pendiente aprobación':
            case 'pendiente':
                return renderBadge('En revisión', 'mdi-light:clock');
            case 'Pendiente carga':
                return renderBadge('No presentado', 'mdi-light:alert-circle');
            case 'Rechazado':
            case 'Rechazada':
                return renderBadge('Rechazado', 'ph:x-circle-thin');
            case 'Aceptado':
            case 'Aceptada':
                return renderBadge('Aceptado', 'circum:circle-check');
            case 'loading':
                return renderBadge('Aguarda ... ', 'mdi-light:clock');
            default:
                return null;
        }
    };

    const savePdf = async (doc, values) => {
        const file = new FormData();
        file.append('doc', doc);
        file.append('document_type', documentType);

        if (documentType === 'Pasaporte') {
            file.append('name', values?.name);
            file.append('lastName', values?.lastName);
            file.append('country', values?.country);
            file.append('nationality', values?.nationality);
            file.append('passportNumber', values?.passportNumber);
            file.append('passportExpiration', values?.passportExpiration);
            file.append('birthdate', values?.birthdate);
            file.append('gender', values?.gender);
            file.append('document', values?.document);
        }

        setOpenAwaitModal(true);
        if (data.state === 'Rechazada' || data.state === 'Rechazado') {
            updatePdf(doc, values);
        } else {
            try {
                const res = await saveDocument(file);
                if (res.status !== 200) {
                    setOpenAwaitModal(false);
                    setOpenErrorModal({
                        isOpen: true,
                        messageError: '',
                    });
                } else {
                    if (
                        documentType === 'Pasaporte' &&
                        res?.data?.document?.passport_data
                    ) {
                        setOpenAwaitModal(false);
                        setOpenReviewData({
                            isOpen: true,
                            passportData: res?.data?.document?.passport_data,
                        });
                    } else {
                        setOpenAwaitModal(false);
                        setOpenSuccesModal({
                            isOpen: true,
                            state: res?.data?.document?.state,
                            messageError: res?.data?.document?.ai_review_reason,
                        });
                    }
                }
            } catch (error) {
                setOpenAwaitModal(false);
                setOpenErrorModal(true);
            }
        }
        dispatch(documentsActions.getDocumentsRequest());
    };

    const updatePdf = async (doc, values) => {
        const file = new FormData();
        file.append('doc', doc);
        file.append('document_type', documentType);

        if (documentType === 'Pasaporte') {
            file.append('name', values?.name);
            file.append('lastName', values?.lastName);
            file.append('country', values?.country);
            file.append('nationality', values?.nationality);
            file.append('passportNumber', values?.passportNumber);
            file.append('passportExpiration', values?.passportExpiration);
            file.append('birthdate', values?.birthdate);
            file.append('gender', values?.gender);
            file.append('document', values?.document);
        }

        setOpenAwaitModal(true);
        try {
            const res = await updateDocument({ file: file, id: data.id });
            if (res.status !== 200) {
                setOpenAwaitModal(false);
                setOpenErrorModal(true);
            } else {
                if (
                    documentType === 'Pasaporte' &&
                    res?.data?.document?.passport_data
                ) {
                    setOpenAwaitModal(false);
                    setOpenReviewData({
                        isOpen: true,
                        passportData: res?.data?.document?.passport_data,
                    });
                } else {
                    setOpenAwaitModal(false);
                    setOpenSuccesModal({
                        isOpen: true,
                        state: res?.data?.document?.state,
                        messageError: res?.data?.document?.ai_review_reason,
                    });
                }
            }
        } catch (error) {
            setOpenAwaitModal(false);
            setOpenErrorModal(true);
        }
        dispatch(documentsActions.getDocumentsRequest());
    };

    return (
        <>
            {!!data && (
                <>
                    <Container>
                        <Header>
                            <Text fontWeight="700" color="#555555">
                                {data?.document_type}
                            </Text>
                        </Header>
                        <Body>
                            <Left>
                                <TextState
                                    fontWeight="400"
                                    fontSize="15px"
                                    color="#222222"
                                    letterSpacing="0.1%"
                                    lineHeight="28px"
                                >
                                    Estado:
                                </TextState>
                                {loading
                                    ? getBadgeContent('loading')
                                    : getBadgeContent(data?.state)}
                            </Left>

                            <ButtonsWrapper>
                                {stateDocumentToSign &&
                                data?.state.toLowerCase() !== 'aceptada' &&
                                data?.state.toLowerCase() !== 'aceptado' &&
                                data?.state.toLowerCase() !== 'pendiente' &&
                                data?.state.toLowerCase() !==
                                    'pendiente aprobación' ? (
                                    <SignatureButton
                                        onClick={handleSignatureClick}
                                    >
                                        <Tooltip
                                            title={'Firmar documento'}
                                            placement="top"
                                            arrow
                                        >
                                            <FontAwesomeIcon
                                                icon="fa-regular fa-signature"
                                                color="#b13d15"
                                                size={20}
                                            />
                                        </Tooltip>
                                    </SignatureButton>
                                ) : null}
                                <RequirementsButton
                                    onClick={handleViewRequirements}
                                >
                                    <Tooltip
                                        title="Requerimientos"
                                        placement="top"
                                        arrow
                                    >
                                        <Icon
                                            icon="uil:file-info-alt"
                                            width="24"
                                            height="24"
                                            onClick={handleViewRequirements}
                                        />
                                    </Tooltip>
                                </RequirementsButton>

                                {handleActionButton(
                                    data?.state,
                                    documentId,
                                    documentName
                                )}
                            </ButtonsWrapper>
                        </Body>
                    </Container>

                    {openRequirementsModal && (
                        <DocumentRequirementsModal
                            open={openRequirementsModal}
                            handleCloseModal={handleCloseViewRequirements}
                            title={data?.name}
                            list={requirementsDetail}
                            type={documentType}
                            exampleUrl={exampleFile}
                            downloadUrl={downloadFile}
                            documentId={documentId}
                            setLoading={setLoading}
                            openSnackbarText={openSnackbarText}
                            documentTypeId={documentTypeId}
                            documentName={documentName}
                            stateDocumentToSign={stateDocumentToSign}
                        />
                    )}

                    {openSignModal && (
                        <DocumentRequirementsSign
                            open={openSignModal}
                            handleClose={() => setOpenSignModal(false)}
                            documentTypeId={documentTypeId}
                            title={documentName}
                        />
                    )}

                    {(openUpdateDocumentModal || openIaUpdateModal) &&
                    revision_id ? (
                        <IaUploadDocumentsModal
                            open={openIaUpdateModal}
                            handleCloseModal={() => setOpenIaUpdateModal(false)}
                            handleSave={savePdf}
                            title={data?.name}
                            documentType={documentType}
                            state={data?.state}
                            comments={data?.deny_reason}
                            documentId={documentId}
                            denyMessage={data?.ai_review_reason}
                        />
                    ) : (
                        <UpdateDocumentsModal
                            open={openUpdateDocumentModal}
                            handleCloseModal={() =>
                                setOpenUpdateDocumentModal(false)
                            }
                            handleSave={updatePdf}
                            title={data?.name}
                            documentId={documentId}
                            id={data?.id}
                            setLoading={setLoading}
                            openSnackbarText={openSnackbarText}
                            comments={data?.deny_reason}
                            dateReview={data?.date}
                        />
                    )}

                    {(openUploadDocumentModal || openIaDocumentModal) &&
                    revision_id ? (
                        <IaUploadDocumentsModal
                            open={openIaDocumentModal}
                            handleCloseModal={() =>
                                setOpenIaDocumentModal(false)
                            }
                            handleSave={savePdf}
                            title={data?.name}
                            documentType={documentType}
                        />
                    ) : (
                        <UploadDocumentsModal
                            open={openUploadDocumentModal}
                            handleCloseModal={() =>
                                setOpenUploadDocumentModal(false)
                            }
                            handleSave={savePdf}
                            title={data?.name}
                        />
                    )}

                    {openAwaitModal && (
                        <AwaitModal
                            open={openAwaitModal}
                            handleCloseModal={() => {
                                setOpenAwaitModal(false);
                            }}
                            title={data?.name}
                        />
                    )}

                    {openReviewData.isOpen && (
                        <DocumentReviewDataModal
                            open={openReviewData.isOpen}
                            passportData={openReviewData.passportData}
                            id_documento={data?.id}
                            handleCloseModal={() =>
                                setOpenReviewData({
                                    isOpen: false,
                                    passportData: null,
                                })
                            }
                        />
                    )}

                    {openSuccesModal.isOpen && (
                        <SuccessfulUploadModal
                            open={openSuccesModal}
                            messageError={openSuccesModal.messageError}
                            handleCloseModal={() => {
                                setOpenSuccesModal({
                                    isOpen: false,
                                    state: '',
                                    messageError: '',
                                });
                            }}
                            state={openSuccesModal.state.toLowerCase()}
                        />
                    )}

                    {openErrorModal.isOpen && (
                        <ErrorUploadModal
                            open={openErrorModal.isOpen}
                            messageError={openErrorModal.messageError}
                            handleCloseModal={() => {
                                setOpenErrorModal({
                                    isOpen: false,
                                    messageError: '',
                                });
                            }}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default Document;

const Container = styled.div`
    height: 180px;
    background-color: #ffffff;
    border-radius: 20px;
    margin-bottom: 1rem;
    border: 1px solid #c4c4c4;
    @media (max-width: 380px) {
        max-width: 250px;
        height: 200px;
    }
`;

const Header = styled.div`
    height: 60%;
    width: calc(80% - 20px);
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    @media (max-width: 380px) {
        height: 80px;
    }
`;

const Body = styled.div`
    height: calc(40% - 2rem);
    width: calc(100% - 2rem);
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #f1f1f1;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    @media (max-width: 380px) {
        flex-direction: column;
        height: calc(120px - 2rem);
    }
`;

const Left = styled.div`
    display: flex;
    flex-direction: row;
    width: 60%;
    justify-content: flex-start;
    align-items: center;
    gap: 0.25rem;
    @media (max-width: 380px) {
        width: 100%;
        justify-content: center;
    }
`;

const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-direction: row;
    width: 40%;
    gap: 0.25rem;
    @media (max-width: 380px) {
        width: 100%;
        justify-content: center;
    }
`;

const FileActionsButton = styled.div`
    width: calc(40px - 16px);
    height: calc(40px - 16px);
    padding: 8px;
    border-radius: 100px;
    border: 1.5px solid #cd2118;
    background-color: #cd2118;
    color: #fafafa;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const RequirementsButton = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid #cd2118;
    color: #cd2118;
    cursor: pointer;
`;

const SignatureButton = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid #cd2118;
    color: #cd2118;
    background-color: transparent;
    cursor: pointer;
`;
